import dayjs from 'dayjs'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { saveAs } from 'file-saver'
import useEnergyGraphFilterStyles from './styles'

const EnergyGraphFilter = ({
  totalEnergygraphDuration,
  graphTotalType,
  setOpenTable,
  handleChooseDate,
  technicalToDate,
  technicalFromDate,
  handleOpen,
  openTable,
  mapTotalenergySeries,
  setTechnicalFromDate,
  setTechnicalToDate,
  chartRef,
  handleTotalenergyGraph,
  handleGraphupdateOptions,
}) => {
  const classes = useEnergyGraphFilterStyles()
  // Remove datepickerDisable state - always allow date selection
  
  const exportToCSV = (rows) => {
    const headings = ['Date', ...rows.map((item) => item.name)]
    const csvContent = [headings.join(',')]
    const allDates = new Set()
    rows.forEach((item) =>
      item.data.forEach((row) =>
        allDates.add(dayjs(row.x).format('YYYY/MM/DD'))
      )
    )
    allDates.forEach((date) => {
      const rowData = [date]
      rows.forEach((item) => {
        const dataForDate = item.data.find(
          (row) => dayjs(row.x).format('YYYY/MM/DD') === date
        )
        rowData.push(dataForDate ? dataForDate.y : 0)
      })
      csvContent.push(rowData.join(','))
    })
    const blob = new Blob([csvContent.join('\n')], {
      type: 'text/csv;charset=utf-8',
    })
    saveAs(blob, 'Energy.csv')
  }

  const handleselectButton = (ButtonType) => {
    let fromDate, toDate

    switch (ButtonType) {
      case 'year':
        fromDate = dayjs(dayjs(new Date())).subtract(11, 'month')
        break
      case '1m':
        fromDate = dayjs(dayjs(new Date())).subtract(29, 'day')
        break
      case '3m':
        fromDate = dayjs(dayjs(new Date())).subtract(2, 'month')
        break
      case '6m':
        fromDate = dayjs(dayjs(new Date())).subtract(5, 'month')
        break
      case 'mtd':
        fromDate = dayjs(new Date()).startOf('month')
        break
      case 'ytd':
        fromDate = dayjs(new Date()).startOf('year')
        break
      case 'day':
        fromDate = technicalFromDate
        break
      case 'custom':
        // Use current date picker values for custom
        fromDate = technicalFromDate
        toDate = technicalToDate
        break
      default:
        break
    }

    if (ButtonType !== 'custom') {
      toDate = dayjs(new Date())
      setTechnicalFromDate(fromDate)
      setTechnicalToDate(toDate)
    }
    
    // Always pass the actual dates to the API, but use ButtonType for display logic
    handleTotalenergyGraph(graphTotalType, ButtonType, fromDate, toDate)
  }

  // Enhanced date change handler that automatically switches to custom mode
  const handleDateChange = (value, type) => {
    handleChooseDate(value, type)
    
    // If user changes date while not in custom mode, switch to custom mode
    if (totalEnergygraphDuration !== 'custom') {
      // Delay the switch to custom to allow the date state to update first
      setTimeout(() => {
        handleTotalenergygraphSort('custom')
      }, 100)
    }
  }

  const handleTotalenergygraphSort = (type) => {
    // Remove datepicker disable logic - always keep enabled
    let tickAmount, rotateFormat, tooltipFormat, buttonType

    switch (type) {
      case 'year':
        tickAmount = 12
        rotateFormat = 'MMM/YY'
        tooltipFormat = 'MMMM/YYYY'
        buttonType = 'year'
        break
      case '1m':
        tickAmount = 15
        rotateFormat = 'DD/MM'
        tooltipFormat = 'DD/MMMM/YYYY'
        buttonType = '1m'
        break
      case '3m':
      case '6m':
        tickAmount = type === '3m' ? 3 : 6
        rotateFormat = 'MMM/YY'
        tooltipFormat = 'MMMM/YYYY'
        buttonType = type
        break
      case 'ytd':
        tickAmount = 15
        rotateFormat = 'MMM/YY'
        tooltipFormat = 'MMMM/YYYY'
        buttonType = type
        break
      case 'mtd':
        tickAmount = 15
        rotateFormat = 'DD/MM'
        tooltipFormat = 'DD/MMMM/YYYY'
        buttonType = type
        break
      case 'day':
        tickAmount = 15
        rotateFormat = 'DD/MM'
        tooltipFormat = 'DD/MMMM/YYYY'
        buttonType = 'day'
        setTechnicalFromDate(dayjs(new Date()))
        setTechnicalToDate(dayjs(new Date()))
        break
      case 'custom':
        tickAmount = 15
        rotateFormat = 'DD/MM'
        tooltipFormat = 'DD/MMMM/YYYY'
        buttonType = 'custom'
        // No need to enable/disable date picker - always enabled
        break
      default:
        return
    }

    const newOptions = {
      xaxis: {
        tickAmount: tickAmount || 15,
        tickPlacement: 'on',
        labels: {
          rotateAlways: true,
          formatter: (value, timestamp, opts) => {
            return dayjs(value).format(rotateFormat)
          },
        },
      },
      tooltip: {
        enabled: true,
        x: {
          show: true,
          format: 'MMMM',
          formatter: (value, opts) => {
            return dayjs(
              opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].x
            ).format(tooltipFormat)
          },
        },
      },
    }

    if (!openTable) {
      handleGraphupdateOptions(newOptions)
    }

    handleselectButton(buttonType)
  }

  const datePickerStyle = {
    width: '130px',
  }

  return (
    <div className={classes.root}>
      <div className="align_btw mb_10">
        <div className="h1">Total Energy</div>
        <div>
          <div className="align_end">
            <div class="check_graph mr_8">
              <input
                id="EnergyGraph"
                type="checkbox"
                checked={openTable}
                onChange={() => setOpenTable(!openTable)}
              />
              <label for="EnergyGraph"></label>
            </div>
            <div className="mr_8">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={['DatePicker']}>
                  <div style={datePickerStyle}>
                    <DatePicker
                      style={{ width: '120px !important' }}
                      label="From Date"
                      minDate={dayjs(new Date()).subtract(2, 'year')}
                      maxDate={
                        totalEnergygraphDuration === 'custom'
                          ? technicalToDate
                          : dayjs()
                      }
                      className={classes.datePicker}
                      format="DD-MM-YYYY"
                      value={technicalFromDate}
                      onChange={(value) => handleDateChange(value, 'from')}
                      disabled={false} // Always enabled
                    />
                  </div>
                </DemoContainer>
              </LocalizationProvider>
            </div>
            <div className="mr_8">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={['DatePicker']}>
                  <div style={datePickerStyle}>
                    <DatePicker
                      style={{ width: '120px !important' }}
                      label="To Date"
                      maxDate={dayjs()}
                      minDate={
                        totalEnergygraphDuration === 'custom'
                          ? technicalFromDate
                          : dayjs(new Date()).subtract(2, 'year')
                      }
                      className={classes.datePicker}
                      format="DD-MM-YYYY"
                      value={technicalToDate}
                      onChange={(value) => handleDateChange(value, 'to')}
                      disabled={false} // Always enabled
                    />
                  </div>
                </DemoContainer>
              </LocalizationProvider>
            </div>
            {openTable && (
              <div className="mr_8">
                <VerticalAlignBottomIcon
                  className="i_clr pointer"
                  onClick={() => exportToCSV(mapTotalenergySeries)}
                />
              </div>
            )}
            <div className="mt8">
              <FullscreenIcon className="i_clr pointer" onClick={handleOpen} />
            </div>
          </div>
        </div>
      </div>
      <div className="align_end mb_10">
        <div>
          <div
            className={
              totalEnergygraphDuration === 'custom'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('custom')}
          >
            CUST
          </div>
          <div
            className={
              totalEnergygraphDuration === 'ytd'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('ytd')}
          >
            YTD
          </div>
          <div
            className={
              totalEnergygraphDuration === 'mtd'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('mtd')}
          >
            MTD
          </div>
          <div
            className={
              totalEnergygraphDuration === 'year'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('year')}
          >
            1 Y
          </div>
          <div
            className={
              totalEnergygraphDuration === '6m'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('6m')}
          >
            6 M
          </div>
          <div
            className={
              totalEnergygraphDuration === '3m'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('3m')}
          >
            3 M
          </div>
          <div
            className={
              totalEnergygraphDuration === '1m'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('1m')}
          >
            1 M
          </div>
        </div>
      </div>
    </div>
  )
}

EnergyGraphFilter.propTypes = {}

export default EnergyGraphFilter
